import { useState, useEffect, useRef } from 'react'
import { Search, User, Plus, UserCircle, Loader2, X } from 'lucide-react'
import { CustomerCard, CustomerRowCard } from './CustomerCard'
import { AddCustomerModal } from './AddCustomerModal'
import { CustomerModal } from './CustomerModal'
import { usePOSStore } from '@/stores/posStore'
import { useCustomerSearch, useRecentCustomers } from '@/api/customers'
import { useDebounce, useSelectCustomer } from '@/hooks'
import type { Customer, Product } from '@/types'

export interface CustomersTabProps {
  onShopByBrand?: (brandName: string) => void
  onShopByCategory?: (categoryName: string) => void
  onOpenProduct?: (product: Product) => void
}

export function CustomersTab({
  onShopByBrand,
  onShopByCategory,
  onOpenProduct,
}: CustomersTabProps = {}) {
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 300)
  const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false)
  const [detailsCustomer, setDetailsCustomer] = useState<Customer | null>(null)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    searchInputRef.current?.focus()
  }, [])

  const { viewMode, selectedCustomer, setActiveTab } = usePOSStore()
  const selectCustomer = useSelectCustomer()


  const { data: searchResults = [], isFetching: isSearchFetching } = useCustomerSearch(debouncedSearchQuery, debouncedSearchQuery.length >= 2)
  const { data: recentCustomers = [] } = useRecentCustomers(50)

  const trimmedQuery = searchQuery.trim()
  const isSearchingForCustomers =
    trimmedQuery.length >= 2 &&
    (debouncedSearchQuery !== trimmedQuery || isSearchFetching)

  const customers = debouncedSearchQuery.length >= 2 ? searchResults : recentCustomers

  // Tab transitions go in `onSuccess` so a blocked customer change
  // (e.g. partial-payment lock in useSelectCustomer) doesn't navigate
  // the cashier away. Without the gate, mutate() fires-and-forgets and
  // the `setActiveTab` runs synchronously regardless of outcome --
  // toast + nav contradict each other.
  //
  // Each pick blurs the (mount-autofocused) search input BEFORE the
  // mutation: the select is async and the tab only switches on success,
  // so a scan fired during the in-flight window would otherwise type
  // into this input instead of reaching the scanner listener
  // (docs/scan-focus-plan.md, mechanism 1).
  const handleSelectCustomer = (customer: Customer) => {
    searchInputRef.current?.blur()
    selectCustomer.mutate(customer, {
      onSuccess: () => setActiveTab('products'),
    })
  }

  const handleSelectGuestCustomer = () => {
    searchInputRef.current?.blur()
    selectCustomer.mutate(null, {
      onSuccess: () => setActiveTab('products'),
    })
  }

  const handleAddCustomer = (customer: Customer) => {
    searchInputRef.current?.blur()
    selectCustomer.mutate(customer, {
      onSuccess: () => {
        setActiveTab('products')
        setIsAddCustomerModalOpen(false)
      },
    })
  }


  useEffect(() => {
    if (focusedIndex >= 0 && containerRef.current) {
      const child = containerRef.current.children[focusedIndex] as HTMLElement
      child?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [focusedIndex])


  useEffect(() => {
    setFocusedIndex(-1)
  }, [customers])

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Customer Search - Embedded Bar */}
      <div className="bg-white border-b border-gray-200 py-3 px-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Search size={18} className="text-gray-400 flex-shrink-0" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search by name, phone, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 py-1 bg-transparent border-none outline-none text-gray-900 placeholder-gray-400"
          />
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery('')
                searchInputRef.current?.focus()
              }}
              aria-label="Clear search"
              className="flex items-center justify-center p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer flex-shrink-0"
            >
              <X size={16} />
            </button>
          )}
          <button
            onClick={handleSelectGuestCustomer}
            className="flex items-center gap-1.5 px-2 md:px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors cursor-pointer flex-shrink-0"
          >
            <UserCircle size={16} />
            <span className="hidden md:inline">Guest</span>
          </button>
          <button
            onClick={() => setIsAddCustomerModalOpen(true)}
            className="flex items-center gap-1.5 px-2 md:px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer flex-shrink-0"
          >
            <Plus size={16} />
            <span className="hidden md:inline">New Customer</span>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* Searching state */}
        {isSearchingForCustomers && (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <Loader2 size={28} className="mb-2 animate-spin" />
            <p className="font-medium">Searching for "{trimmedQuery}"...</p>
          </div>
        )}

        {/* Customer Results - Grid View */}
        {!isSearchingForCustomers && viewMode === 'grid' && customers.length > 0 && (
          <div ref={containerRef} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3 mb-4">
            {customers.map((cust, index) => (
              <CustomerCard
                key={cust.id}
                customer={cust}
                isSelected={selectedCustomer?.id === cust.id}
                onSelect={handleSelectCustomer}
                onOpenDetails={setDetailsCustomer}
                isFocused={index === focusedIndex}
              />
            ))}
          </div>
        )}

        {/* Customer Results - List View */}
        {!isSearchingForCustomers && viewMode === 'list' && customers.length > 0 && (
          <div ref={containerRef} className="space-y-3 mb-4">
            {customers.map((cust, index) => (
              <CustomerRowCard
                key={cust.id}
                customer={cust}
                isSelected={selectedCustomer?.id === cust.id}
                onSelect={handleSelectCustomer}
                onOpenDetails={setDetailsCustomer}
                isFocused={index === focusedIndex}
              />
            ))}
          </div>
        )}

        {!isSearchingForCustomers && customers.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-gray-300">
            <User size={32} className="mb-2" />
            <p className="font-medium">
              {searchQuery ? 'No customers found' : 'Start typing to search customers'}
            </p>
          </div>
        )}
      </div>

      {/* Add Customer Modal */}
      {isAddCustomerModalOpen && (
        <AddCustomerModal
          onClose={() => setIsAddCustomerModalOpen(false)}
          onSuccess={handleAddCustomer}
        />
      )}

      {/* Customer Details Modal */}
      {detailsCustomer && (
        <CustomerModal
          customer={detailsCustomer}
          onClose={() => setDetailsCustomer(null)}
          onShopByBrand={onShopByBrand}
          onShopByCategory={onShopByCategory}
          onOpenProduct={onOpenProduct}
        />
      )}
    </div>
  )
}
